import React, { useEffect, useState } from "react";
import { MdAdd, MdCheck, MdInbox, MdWarningAmber } from "react-icons/md";
import { getTasksByProject } from "../../../../services/projectTaskApi";
import { addAllocation } from "../../../../services/capacityApi";
import { ProjectTask } from "../../../../interfaces/ProjectTask";

interface Option {
  id: number;
  name: string;
}

interface Props {
  projects: Option[];
  onChanged: () => void;
}

const inputCls =
  "w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-800 transition-all focus:border-dv-blue-500 focus:outline-none focus:ring-2 focus:ring-dv-blue-500/20 dark:border-white/10 dark:bg-navy-900 dark:text-white";

const IncludeTasksInWeek: React.FC<Props> = ({ projects, onChanged }) => {
  const [projectId, setProjectId] = useState<string>("");
  const [week, setWeek] = useState<string>("");
  const [tasks, setTasks] = useState<ProjectTask[]>([]);
  const [loading, setLoading] = useState(false);
  const [includedIds, setIncludedIds] = useState<number[]>([]);

  useEffect(() => {
    if (!projectId) {
      setTasks([]);
      return;
    }
    setLoading(true);
    setIncludedIds([]);
    getTasksByProject(Number(projectId))
      .then((all) =>
        // Só tarefas com responsável + estimativa e não concluídas/canceladas.
        setTasks(
          all.filter(
            (t) =>
              t.assigneeEmployeeId != null &&
              (t.estimatedHours ?? 0) > 0 &&
              t.statusCategory !== "Done" &&
              t.statusCategory !== "Cancelled" &&
              t.statusCategory !== "OnHold"
          )
        )
      )
      .catch((e) => console.error("Erro ao carregar tarefas", e))
      .finally(() => setLoading(false));
  }, [projectId]);

  const include = async (task: ProjectTask) => {
    if (!week || task.assigneeEmployeeId == null) return;
    try {
      await addAllocation({
        employeeId: task.assigneeEmployeeId,
        projectId: Number(projectId),
        weekStart: week,
        allocatedHours: task.estimatedHours ?? 0,
      });
      setIncludedIds((prev) => [...prev, task.id]);
      onChanged();
    } catch (e) {
      console.error("Erro ao incluir tarefa no capacity", e);
    }
  };

  return (
    <section className="rounded-2xl border border-gray-100 p-4 dark:border-white/10">
      <h4 className="mb-1 text-sm font-bold text-navy-700 dark:text-white">
        Incluir tarefas na semana (consumir capacity)
      </h4>
      <p className="mb-3 text-xs text-gray-700 dark:text-gray-300">
        Escolha um projeto e uma semana, e inclua tarefas existentes — cada tarefa cria uma alocação
        do responsável com as horas estimadas, consumindo o capacity da semana.
      </p>

      <div className="mb-3 flex flex-col flex-wrap items-stretch gap-2.5 sm:flex-row sm:items-end">
        <div className="flex w-full flex-col sm:w-56">
          <label htmlFor="include-tasks-project" className="mb-1 text-sm font-semibold text-gray-700 dark:text-gray-300">
            Projeto <span className="text-red-500">*</span>
          </label>
          <select id="include-tasks-project" className={inputCls} value={projectId} onChange={(e) => setProjectId(e.target.value)}>
            <option value="">Projeto…</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>
        <div className="flex w-full flex-col sm:w-56">
          <label htmlFor="include-tasks-week" className="mb-1 text-sm font-semibold text-gray-700 dark:text-gray-300">
            Semana <span className="text-red-500">*</span>
          </label>
          <input id="include-tasks-week" type="date" className={inputCls} value={week} onChange={(e) => setWeek(e.target.value)} />
        </div>
      </div>

      {projectId && (
        <div className="overflow-x-auto rounded-2xl border border-gray-100 bg-white nina-surface shadow-dv-md dark:border-white/10 dark:bg-navy-800">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-navy-900">
              <tr>
                <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300">Tarefa</th>
                <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300">Responsável</th>
                <th className="px-4 py-3 text-right text-[11px] font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300">Estimativa</th>
                <th className="w-28 px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={4} className="px-4 py-5 text-center text-gray-700 dark:text-gray-300">
                    Carregando...
                  </td>
                </tr>
              )}
              {!loading &&
                tasks.map((t) => {
                  const done = includedIds.includes(t.id);
                  return (
                    <tr key={t.id} className="border-b border-gray-100 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-navy-700">
                      <td className="px-4 py-3 font-medium text-navy-700 dark:text-white">{t.title}</td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{t.assigneeName ?? "—"}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-gray-700 dark:text-gray-300">{t.estimatedHours}h</td>
                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          onClick={() => include(t)}
                          disabled={!week || done}
                          className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold transition-all active:scale-[.98] ${
                            done
                              ? "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300"
                              : "dv-sheen bg-dv-gradient text-white shadow-dv-sm disabled:cursor-not-allowed disabled:opacity-40"
                          }`}
                        >
                          {done ? (
                            <>
                              <MdCheck size={14} aria-hidden="true" /> Incluída
                            </>
                          ) : (
                            <>
                              <MdAdd size={14} aria-hidden="true" /> Incluir
                            </>
                          )}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              {!loading && tasks.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-6 text-center text-gray-700 dark:text-gray-300">
                    <span className="inline-flex items-center gap-2">
                      <MdInbox size={18} aria-hidden="true" className="text-gray-400" />
                      Nenhuma tarefa com responsável e estimativa neste projeto.
                    </span>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      {!week && projectId && (
        <p className="mt-2 flex items-center gap-1 text-[11px] font-medium text-amber-600 dark:text-amber-400">
          <MdWarningAmber size={14} aria-hidden="true" /> Selecione a semana para habilitar a inclusão.
        </p>
      )}
    </section>
  );
};

export default IncludeTasksInWeek;
